import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Tenants } from '@/lib/api/v1';
import { Button, Card, Input, Spinner, Icon } from '@/design-system';
import { useSession, useToasts, usePrefs } from '@/lib/store';
import { Logo } from '@/components/Logo';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { setSession, memberships } = useSession();
  const { setActiveTenantId } = usePrefs();
  const { push } = useToasts();
  const [name, setName] = useState('Min vinsamling');

  const create = useMutation({
    mutationFn: () =>
      Tenants.create({ name, slug: name.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').slice(0, 60) }),
    onSuccess: (data) => {
      const newMemberships = [...memberships, data.membership];
      setSession({ user: useSession.getState().user, memberships: newMemberships });
      setActiveTenantId(data.tenant.id);
      push({ title: 'Husholdning opprettet', description: data.tenant.name, tone: 'success' });
      navigate('/cellar', { replace: true });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke opprette', description: err.message, tone: 'danger' }),
  });

  return (
    <div className="relative grid min-h-screen place-items-center px-4">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-1/3 h-96 w-96 rounded-full bg-candle/15 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-[28rem] w-[28rem] rounded-full bg-ruby/10 blur-3xl" />
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-lg">
        <Card glow className="grid gap-6 p-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <Logo size={40} showWordmark={false} />
            <h1 className="font-serif text-3xl text-parchment">Lag din første vinsamling</h1>
            <p className="text-sm text-mist max-w-md">
              Hver husholdning er en separat kjeller. Du kan invitere medlemmer senere.
            </p>
          </div>

          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Input
              iconLeft="container"
              label="Navn på husholdningen"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              maxLength={60}
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={create.isPending}
              iconRight="arrow-right"
            >
              Opprett kjeller
            </Button>
          </form>

          <div className="grid gap-2 rounded-lg border border-line bg-ink-soft p-4 text-xs text-mist">
            <div className="flex items-center gap-2 text-candle">
              <Icon name="info" size={14} /> <span className="font-semibold uppercase tracking-wider">Tips</span>
            </div>
            <p>
              Du kan opprette flere husholdninger senere — for eksempel "Sommerhuset" eller "Restauranten" — og bytte
              mellom dem fra menyen.
            </p>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
